/**
 * agents.ts — maps the orchestrator's subagent/intent signals to the colored
 * "agent step" chips shown during a turn.
 *
 * `reduceTrace` folds the live `trace` frames (intent_classified, then
 * subagent_dispatched) into the step timeline; it inspects fields defensively
 * and never throws. `agentsFromFinal` backfills from final.data.{intent,
 * subagents_used} when dispatch traces were absent.
 */
import { TraceEvent, FinalData, CopilotStep } from './copilotTypes'

export interface AgentDef {
  key: string
  label: string
  working: string
  color: string
}

export const AGENTS: Record<string, AgentDef> = {
  reviews: { key: 'reviews', label: 'Reviews Agent', working: 'Gathering reviews', color: '#22c55e' },
  bookings: { key: 'bookings', label: 'Bookings Agent', working: 'Collecting bookings', color: '#3b82f6' },
  payments: { key: 'payments', label: 'Payments Agent', working: 'Checking payments', color: '#ef4444' },
  estimates: { key: 'estimates', label: 'Estimates Agent', working: 'Reviewing estimates', color: '#14b8a6' },
  leads: { key: 'leads', label: 'Leads Agent', working: 'Scanning new leads', color: '#f59e0b' },
  profile: { key: 'profile', label: 'Presence Agent', working: 'Checking your online presence', color: '#8b5cf6' }
}

/** synonyms the backend may use → our agent key */
const ALIASES: Record<string, string> = {
  review: 'reviews',
  reviews: 'reviews',
  booking: 'bookings',
  bookings: 'bookings',
  appointment: 'bookings',
  appointments: 'bookings',
  invoice: 'payments',
  invoices: 'payments',
  payment: 'payments',
  payments: 'payments',
  billing: 'payments',
  estimate: 'estimates',
  estimates: 'estimates',
  quote: 'estimates',
  lead: 'leads',
  leads: 'leads',
  contact: 'leads',
  profile: 'profile',
  seo: 'profile',
  gmb: 'profile',
  business_profile: 'profile'
}

const resolveAgent = (token?: unknown): AgentDef | undefined => {
  if (typeof token !== 'string') return undefined
  const key = ALIASES[token.trim().toLowerCase()]
  return key ? AGENTS[key] : undefined
}

const asString = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

let stepSeq = 0
const nextId = () => `step_${++stepSeq}`

const UNDERSTANDING_LABEL = 'Understanding your request'
const understandingStep = (done = false): CopilotStep => ({
  id: nextId(),
  kind: 'generic',
  label: UNDERSTANDING_LABEL,
  done
})

/** seeded the moment a turn starts (before any trace) so the UI shows progress immediately */
export const initialSteps = (): CopilotStep[] => [understandingStep()]

/**
 * Fold a trace event into the running agent-step timeline.
 *
 *   intent_classified    → start with an "Understanding your request" step
 *   subagent_dispatched  → append a colored agent chip per dispatched subagent
 *                          (event.payload.subagent), marking "Understanding" done
 *   subagent_completed   → mark the matching agent chip done (by step_id, else by
 *                          resolved subagent key)
 *
 * Parallel subagents stay spinning until their `subagent_completed` (or `final`)
 * arrives. Other trace kinds are ignored for now (the backend will emit
 * purpose-built messages later).
 */
export const reduceTrace = (prev: CopilotStep[], event: TraceEvent): CopilotStep[] => {
  if (!event || typeof event !== 'object') return prev
  const e = event as Record<string, unknown>
  const kind = asString(e.kind)
  const payload = (e.payload && typeof e.payload === 'object' ? e.payload : {}) as Record<string, unknown>

  if (kind === 'intent_classified') {
    return prev.length ? prev : [understandingStep()]
  }

  if (kind === 'subagent_dispatched') {
    const agent = resolveAgent(payload.subagent)
    if (!agent) return prev
    if (prev.some((s) => s.agentKey === agent.key)) return prev // already dispatched
    const base = prev.length
      ? prev.map((s) => (s.kind === 'generic' ? { ...s, done: true } : s))
      : [understandingStep(true)]
    const stepId = asString(payload.step_id)
    return [
      ...base,
      { id: nextId(), kind: 'agent', label: agent.working, agentKey: agent.key, color: agent.color, stepId }
    ]
  }

  if (kind === 'subagent_completed') {
    const stepId = asString(payload.step_id)
    const agent = resolveAgent(payload.subagent)
    if (!stepId && !agent) return prev
    return prev.map((s) => {
      if (s.kind !== 'agent' || s.done) return s
      const matches = stepId && s.stepId ? s.stepId === stepId : agent && s.agentKey === agent.key
      return matches ? { ...s, done: true } : s
    })
  }

  return prev
}

/** derive agent steps from final.data when traces were sparse/opaque */
export const agentsFromFinal = (data?: FinalData): CopilotStep[] => {
  const out: CopilotStep[] = []
  const seen = new Set<string>()
  const push = (token?: string) => {
    const a = resolveAgent(token)
    if (a && !seen.has(a.key)) {
      seen.add(a.key)
      out.push({ id: nextId(), kind: 'agent', label: a.working, agentKey: a.key, color: a.color, done: true })
    }
  }
  if (data && Array.isArray(data.subagents_used)) data.subagents_used.forEach(push)
  if (!out.length && data && data.intent) push(data.intent)
  return out
}
